import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { AccommodationRoomTypesQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { useState } from 'react'
import { AccommodationRoomTypeFilters } from '@/types'
import { useTable } from '@/helpers/useTable'
import { Button } from '../base/button'
import { dedupe } from '@/helpers/dedupe'
import { EditDialog } from './edit_dialog'
import { NewDialog } from './new_dialog'

interface Props extends TableProps {
  accommodationId: string
  createable?: boolean
  updateable?: boolean
}

export const Table = ({ pageSize = 10, accommodationId, createable = false, updateable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<AccommodationRoomTypesQuery>(
    Query,
    { variables: { accommodationId, first: pageSize } }
  )
  const {
    pageInfo,
    openNew,
    setOpenNew,
    openEdit,
    setOpenEdit
  } = useTable({ pageInfo: data?.accommodations.nodes[0].accommodationRoomTypes.pageInfo })
  const [accommodationRoomTypeId, setAccommodationRoomTypeId] = useState<string>()

  const editAccommodationRoomType = (id: string) => {
    if (!updateable) return

    setAccommodationRoomTypeId(id)
    setOpenEdit(true)
  }

  if (error) { return <Error message={error.message} /> }
  if (loading || !data) { return <Loading /> }

  return (
    <>
      <BaseTable<AccommodationRoomTypeFilters>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        filters={[]}
        headings={[{ name: 'Name' }, { name: 'Capacity' }, { name: 'Layout' }]}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.accommodations.nodes[0].accommodationRoomTypes.nodes = dedupe([
              ...previousData.accommodations.nodes[0].accommodationRoomTypes.nodes,
              ...fetchMoreResult.accommodations.nodes[0].accommodationRoomTypes.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        rows={data.accommodations.nodes[0].accommodationRoomTypes.nodes.map(accommodationRoomType => (
          {
            id: accommodationRoomType.id,
            columns: [
              accommodationRoomType.name,
              accommodationRoomType.capacity,
              accommodationRoomType.layout,
            ]
          }
        ))}
        onRowClick={editAccommodationRoomType}
      />
      { accommodationRoomTypeId &&
        <EditDialog
          accommodationId={accommodationId}
          id={accommodationRoomTypeId}
          open={openEdit}
          setOpen={setOpenEdit}
        />}
      <NewDialog id={data.accommodations.nodes[0].id} open={openNew} setOpen={setOpenNew} />
    </>
  )
}
